import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Briefcase, FileText, PoundSterling, LogOut, Truck, FileCheck, Settings, ListTodo, Archive, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import TodoPanel from "./TodoPanel";

const mainNavItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testid: "nav-suppliers" },
  { to: "/projects", label: "Projects", icon: Briefcase, testid: "nav-projects" },
  { to: "/proofing", label: "Proofing", icon: FileCheck, testid: "nav-proofing" },
  { to: "/quotes", label: "Quotes", icon: FileText, testid: "nav-quotes" },
  { to: "/invoiced", label: "Invoiced", icon: PoundSterling, testid: "nav-invoiced" },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [todoOpen, setTodoOpen] = useState(false);
  // Sidebar badges:
  //  • Proofing → count of non-archived proofs with at least one *unresolved*
  //    comment or annotation (i.e. designer still has work to do). Distinct
  //    from "new client activity" — this counts open work, not unread updates.
  //  • Quotes   → count of quotes where the client has just made selections.
  const [unresolvedProofsCount, setUnresolvedProofsCount] = useState(0);
  const [unseenQuotesCount, setUnseenQuotesCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchUnseen = async () => {
      try {
        const [proofsRes, quotesRes] = await Promise.allSettled([
          api.get("/proofs/unresolved-count"),
          api.get("/quotes/unseen-selections"),
        ]);
        if (cancelled) return;
        if (proofsRes.status === "fulfilled") {
          const data = proofsRes.value.data;
          setUnresolvedProofsCount(typeof data?.count === "number" ? data.count : 0);
        }
        if (quotesRes.status === "fulfilled") {
          setUnseenQuotesCount(Array.isArray(quotesRes.value.data) ? quotesRes.value.data.length : 0);
        }
      } catch {
        // ignore — not critical
      }
    };
    fetchUnseen();
    const id = setInterval(fetchUnseen, 60_000); // refresh every minute
    // Allow other parts of the app to trigger an immediate refresh.
    const onBump = () => fetchUnseen();
    window.addEventListener("proofs:unseen-refresh", onBump);
    window.addEventListener("quotes:unseen-refresh", onBump);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("proofs:unseen-refresh", onBump);
      window.removeEventListener("quotes:unseen-refresh", onBump);
    };
  }, []);
  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <aside
      data-testid="sidebar"
      className="w-64 border-r border-zinc-200 bg-white h-screen fixed left-0 top-0 flex flex-col"
    >
      <div className="px-4 py-6 flex items-center justify-center">
        <img
          src="/kerboom-logo.png"
          alt="Kerboom"
          className="max-w-[180px] w-full h-auto object-contain"
          data-testid="sidebar-logo"
        />
      </div>

      <div className="px-4 mt-2">
        <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-2">Platform</p>
        <nav className="flex flex-col gap-1">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-zinc-100 text-zinc-950"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/proofing" && unresolvedProofsCount > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none shadow-sm"
                  data-testid="proofing-unresolved-badge"
                  title={`${unresolvedProofsCount} proof${unresolvedProofsCount !== 1 ? "s have" : " has"} unresolved changes`}
                >
                  {unresolvedProofsCount > 99 ? "99+" : unresolvedProofsCount}
                </span>
              )}
              {item.to === "/quotes" && unseenQuotesCount > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold leading-none shadow-sm"
                  data-testid="quotes-unseen-badge"
                  title={`${unseenQuotesCount} quote${unseenQuotesCount !== 1 ? "s have" : " has"} new client selections`}
                >
                  {unseenQuotesCount > 99 ? "99+" : unseenQuotesCount}
                </span>
              )}
            </NavLink>
          ))}
          <a
            href="https://kerboom-archive-search.creative-moj-2282.chatgpt.site/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-kerboom-archive"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
          >
            <Archive className="w-4 h-4" />
            <span className="flex-1">Kerboom Archive</span>
            <ExternalLink className="w-3 h-3 text-zinc-400" />
          </a>
        </nav>
      </div>

      {/* Bottom-pinned utility section: To-Do + Admin */}
      <div className="mt-auto px-4 pb-2">
        <div className="border-t border-zinc-200 pt-3 pb-1">
          <TodoPanel
            open={todoOpen}
            onOpenChange={setTodoOpen}
            trigger={
              <button
                type="button"
                data-testid="nav-todo"
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  todoOpen ? "bg-zinc-100 text-zinc-950" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <ListTodo className="w-4 h-4" />
                To-Do
              </button>
            }
          />
          <NavLink
            to="/admin"
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-zinc-100 text-zinc-950"
                  : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
              }`
            }
          >
            <Settings className="w-4 h-4" />
            Admin
          </NavLink>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-zinc-950 text-white grid place-items-center text-xs font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-950 truncate">{user?.name || "User"}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
